import type { PropertyDataType } from 'types/schema';
import type { RendererMap } from 'types/aspect';

/**
 * The single code-defined per-data-type fallback table `resolveRenderer`'s chain step 4
 * consults (EAINT-855 spec §Per-data-type default table). Every entry is `origin: 'catalog'`
 * naming a registered catalog `type` — `typeDefaultsCoverEveryDataTypeAndMode` (component-catalog
 * spec) asserts both the exhaustiveness over `PropertyDataType` and the ref validity.
 */
export const TYPE_DEFAULT_RENDERERS: Record<PropertyDataType, RendererMap> = {
  string: {
    edit: { origin: 'catalog', name: 'TextField' },
    view: { origin: 'catalog', name: 'Text' },
  },
  number: {
    edit: { origin: 'catalog', name: 'NumberField' },
    view: { origin: 'catalog', name: 'Text' },
  },
  boolean: {
    edit: { origin: 'catalog', name: 'BooleanField' },
    // `GenericDisplay`, not `Text`: `Text` renders its `content` as a React child, and React
    // renders a boolean child as NOTHING — a `false` (or `true`) property showed a blank cell.
    // `GenericDisplayAtom` stringifies, which is also what the pre-unification default did.
    view: { origin: 'catalog', name: 'GenericDisplay' },
  },
  date: {
    edit: { origin: 'catalog', name: 'DateField' },
    view: { origin: 'catalog', name: 'DateTimeDisplay' },
  },
  datetime: {
    edit: { origin: 'catalog', name: 'DateTimeField' },
    view: { origin: 'catalog', name: 'DateTimeDisplay' },
  },
  unit: {
    edit: { origin: 'catalog', name: 'UnitField' },
    // `GenericDisplay`, not `Text`: a `unit` value is a `UnitValue` OBJECT (`{ value, unit }`),
    // and handing an object to a text renderer is not displayable. `GenericDisplayAdapter`
    // formats the pair as `"<value> <unit>"`.
    view: { origin: 'catalog', name: 'GenericDisplay' },
  },
  reference: {
    edit: { origin: 'catalog', name: 'ReferenceField' },
    view: { origin: 'catalog', name: 'SphereCard' },
  },
  array: {
    edit: { origin: 'catalog', name: 'TagListField' },
    view: { origin: 'catalog', name: 'ChipListDisplay' },
  },
  json: {
    edit: { origin: 'catalog', name: 'JsonField' },
    view: { origin: 'catalog', name: 'GenericDisplay' },
  },
};
